// ============================================================================
// input.js — keyboard + mouse (pointer-lock look) + gamepad. Resolves WASD /
// left-stick into camera-relative world-space movement, exposes held states
// (block) and latched one-shot presses (attacks, dodge, titan) that the fixed
// logic consumes exactly once via take*(). Mouse/right-stick feed camera look.
//
//   Move   WASD / left stick        Light (N)  LMB / X
//   Look   mouse / right stick      Charge (C) RMB / Y
//   Dodge  Space / A                Block      Shift / LB (hold)
//   Titan  Q / RT
// ============================================================================

export class Input {
  constructor(domElement) {
    this.dom = domElement;
    this.keys = Object.create(null);
    this.moveX = 0; this.moveZ = 0;
    this.blockHeld = false;
    this.sprint = false;
    this.look = { dx: 0, dy: 0 };
    this.wheel = 0;                 // accumulated wheel delta (camera consumes)
    this.locked = false;
    this.enabled = false;

    this._n = false; this._c = false; this._dodge = false; this._titan = false;
    this._kbBlock = false; this._padBlock = false;
    this._kbSprint = false; this._padSprint = false;
    this._pad = null; this._prevBtn = [];

    window.addEventListener('keydown', (e) => this._key(e, true));
    window.addEventListener('keyup', (e) => this._key(e, false));
    domElement.addEventListener('mousedown', (e) => this._mouse(e));
    domElement.addEventListener('contextmenu', (e) => e.preventDefault());
    document.addEventListener('mousemove', (e) => {
      if (this.locked) { this.look.dx += e.movementX; this.look.dy += e.movementY; }
    });
    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === domElement;
    });
    // mouse-wheel zoom (prevent the page from scrolling while playing)
    domElement.addEventListener('wheel', (e) => {
      if (!this.enabled) return;
      this.wheel += e.deltaY;
      e.preventDefault();
    }, { passive: false });
    domElement.addEventListener('click', () => {
      if (this.enabled && !this.locked && domElement.requestPointerLock) domElement.requestPointerLock();
    });
  }

  requestLock() {
    if (this.dom.requestPointerLock) this.dom.requestPointerLock();
  }

  _key(e, down) {
    const k = e.code;
    if (down && !this.keys[k]) {
      if (k === 'Space') this._dodge = true;
      else if (k === 'KeyQ') this._titan = true;
      else if (k === 'KeyJ') this._n = true;   // keyboard attack fallbacks
      else if (k === 'KeyK') this._c = true;
    }
    this.keys[k] = down;
    if (k === 'ShiftLeft' || k === 'ShiftRight') this._kbSprint = down;  // sprint
    if (k === 'KeyC') this._kbBlock = down;                              // block
    // stop the page from scrolling on space/arrows while playing
    if (this.enabled && (k === 'Space' || k.startsWith('Arrow'))) e.preventDefault();
  }

  _mouse(e) {
    if (!this.enabled) return;
    if (e.button === 0) this._n = true;
    else if (e.button === 2) this._c = true;
  }

  // ---- called once per render frame, before sim + camera ----
  beginFrame(followCam) {
    this._pollGamepad(followCam);
    // keyboard axes
    let ix = (this.keys['KeyD'] ? 1 : 0) - (this.keys['KeyA'] ? 1 : 0);
    let iz = (this.keys['KeyW'] ? 1 : 0) - (this.keys['KeyS'] ? 1 : 0);
    // add left-stick
    if (this._pad) {
      const ax = this._pad.axes[0] || 0, ay = this._pad.axes[1] || 0;
      if (Math.abs(ax) > 0.15) ix += ax;
      if (Math.abs(ay) > 0.15) iz += -ay;
    }
    // clamp magnitude
    const mag = Math.hypot(ix, iz);
    if (mag > 1) { ix /= mag; iz /= mag; }

    // Rotate the raw stick/WASD axes into world space by camera yaw.
    // forward = (sin, cos); screen-right = forward rotated +90° = (-cos, sin).
    const yaw = followCam ? followCam.yaw : 0;
    const fx = Math.sin(yaw), fz = Math.cos(yaw);
    const rx = -Math.cos(yaw), rz = Math.sin(yaw);
    this.moveX = fx * iz + rx * ix;
    this.moveZ = fz * iz + rz * ix;

    this.blockHeld = this._kbBlock || this._padBlock;
    this.sprint = this._kbSprint || this._padSprint;
  }

  endFrame() { this.look.dx = 0; this.look.dy = 0; }

  _pollGamepad(followCam) {
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    let pad = null;
    for (let i = 0; i < pads.length; i++) if (pads[i]) { pad = pads[i]; break; }
    this._pad = pad;
    if (!pad) { this._padBlock = false; this._padSprint = false; return; }
    const b = pad.buttons;
    const pressed = (i) => b[i] && b[i].pressed;
    const edge = (i) => pressed(i) && !this._prevBtn[i];
    if (edge(2)) this._n = true;         // X
    if (edge(3)) this._c = true;         // Y
    if (edge(0)) this._dodge = true;     // A
    if (edge(7)) this._titan = true;     // RT
    this._padBlock = pressed(4) || pressed(6); // LB / LT
    this._padSprint = pressed(10) || pressed(5); // L3 (stick click) or RB
    // right stick look
    const rxA = pad.axes[2] || 0, ryA = pad.axes[3] || 0;
    if (Math.abs(rxA) > 0.15) this.look.dx += rxA * 22;
    if (Math.abs(ryA) > 0.15) this.look.dy += ryA * 18;
    this._prevBtn.length = b.length;
    for (let i = 0; i < b.length; i++) this._prevBtn[i] = pressed(i);
  }

  takeN() { const v = this._n; this._n = false; return v; }
  takeC() { const v = this._c; this._c = false; return v; }
  takeDodge() { const v = this._dodge; this._dodge = false; return v; }
  takeTitan() { const v = this._titan; this._titan = false; return v; }
}
